import {
  Box, InputGroup, InputLeftElement, Input, Flex, Text,
  Spinner, Divider, useOutsideClick
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import Fuse from "fuse.js";

function useDebounced(value, delay = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

const PL_CENTER = { lat: 52.1, lon: 19.4 };

const CITY_INDEX_PL = [
  { name: "Gdańsk",   lat: 54.352, lon: 18.646, region: "pomorskie", country: "PL" },
  { name: "Gdynia",   lat: 54.518, lon: 18.531, region: "pomorskie", country: "PL" },
  { name: "Warszawa", lat: 52.229, lon: 21.012, region: "mazowieckie", country: "PL" },
  { name: "Kraków",   lat: 50.061, lon: 19.938, region: "małopolskie", country: "PL" },
  { name: "Wrocław",  lat: 51.108, lon: 17.038, region: "dolnośląskie", country: "PL" },
  { name: "Poznań",   lat: 52.408, lon: 16.934, region: "wielkopolskie", country: "PL" },
  { name: "Łódź",     lat: 51.759, lon: 19.458, region: "łódzkie", country: "PL" },
  { name: "Szczecin", lat: 53.429, lon: 14.553, region: "zachodniopomorskie", country: "PL" },
  { name: "Lublin",   lat: 51.247, lon: 22.566, region: "lubelskie", country: "PL" },
  { name: "Katowice", lat: 50.264, lon: 19.023, region: "śląskie", country: "PL" },
  { name: "Bydgoszcz",lat: 53.123, lon: 18.008, region: "kujawsko-pomorskie", country: "PL" },
  { name: "Białystok",lat: 53.133, lon: 23.164, region: "podlaskie", country: "PL" },
  { name: "Rzeszów",  lat: 50.041, lon: 21.999, region: "podkarpackie", country: "PL" },
  { name: "Toruń",    lat: 53.013, lon: 18.598, region: "kujawsko-pomorskie", country: "PL" },
];

const EMAIL_PARAM = "office@coblit.com";
const EMAIL_Q = EMAIL_PARAM ? `&email=${encodeURIComponent(EMAIL_PARAM)}` : "";

// helpery
function normalizeTxt(s) {
  return (s || "").normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}
function haversine(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLon/2)**2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
const fuse = new Fuse(CITY_INDEX_PL, {
  keys: ["name"],
  threshold: 0.3,
  ignoreLocation: true,
  isCaseSensitive: false
});
function localCityHintsPL(q, limit=8){
  const nq = normalizeTxt(q);
  if(!nq) return [];
  // preferujemy prefiks, potem fuzzy
  const prefix = CITY_INDEX_PL
      .filter(c=>normalizeTxt(c.name).startsWith(nq))
      .slice(0,limit);
  if(prefix.length>=limit) return prefix.map(mapLocal);

  const more = fuse.search(q).map(r=>r.item)
      .filter(c=>!prefix.some(p=>p.name===c.name))
      .slice(0,limit-prefix.length);
  return [...prefix, ...more].map(mapLocal);
}
function mapLocal(c){
  return {
    kind:"place",
    label:`${c.name}, ${c.region}, ${c.country}`,
    city:c.name, region:c.region, country:c.country,
    lat:c.lat, lon:c.lon,
    source:"local"
  };
}
async function searchPhotonCities(q, signal, { bias, limit = 15, lang = "default" } = {}) {
  const supported = new Set(["default", "en", "de", "fr"]);
  const useLang = supported.has(lang) ? lang : "default";

  const url = new URL("https://photon.komoot.io/api/");
  url.searchParams.set("q", q);
  url.searchParams.set("limit", String(limit));
  url.searchParams.set("lang", useLang);
  url.searchParams.append("layer", "city");
  // jeśli mniejsze miejscowości:
  // url.searchParams.append("layer","village");

  if (bias?.lat && bias?.lon) {
    url.searchParams.set("lat", String(bias.lat));
    url.searchParams.set("lon", String(bias.lon));
  }

  let res = await fetch(url.toString(), { signal, headers: { Accept: "application/json" } });

  // awaryjnie: jeśli kiedyś znowu 400 za język, to bez lang
  if (res.status === 400) {
    const fallbackUrl = new URL(url);
    fallbackUrl.searchParams.delete("lang");
    res = await fetch(fallbackUrl.toString(), { signal, headers: { Accept: "application/json" } });
  }

  const geo = await res.json();
  const feats = Array.isArray(geo?.features) ? geo.features : [];

  return feats.map(f => {
    const p = f.properties || {};
    const city = p.name; // dla city/town Photon ustawia tu nazwę miasta
    const region = p.state || p.county;
    const country = (p.countrycode || "").toUpperCase();
    const [lon, lat] = f.geometry?.coordinates || [];
    return {
      kind: "place",
      label: `${city}${region ? `, ${region}` : ""}${country ? `, ${country}` : ""}`,
      city, region, country, lat, lon,
      source: "photon",
      raw: f
    };
  });
}
function mergeAndSort({local=[], remote=[], q="", bias}){
  const nq = normalizeTxt(q);
  const byKey = new Map();
  const put = (it) => {
    const key = `${normalizeTxt(it.city||it.label)}|${it.country}`;
    const prev = byKey.get(key);
    // preferujemy dokładniejsze (Photon) nad lokalnym, potem krótsza odległość
    const dist = (bias && it.lat && it.lon) ? haversine(bias.lat,bias.lon,it.lat,it.lon) : Infinity;
    const score = (normalizeTxt(it.city||"").startsWith(nq) ? 100 : 0) - Math.min(50, dist/50) + (it.source==="photon"?5:0);
    const cand = {...it,__score:score};
    if(!prev || cand.__score>prev.__score) byKey.set(key,cand);
  };
  local.forEach(put); remote.forEach(put);
  return Array.from(byKey.values())
      .sort((a,b)=> (b.__score - a.__score) || a.label.localeCompare(b.label,"pl"))
      .map(x=>{ delete x.__score; return x; });
}

async function reverseGeocode(lat, lon, signal) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=10&addressdetails=1&accept-language=pl${EMAIL_Q}`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error("Nominatim error");
  const it = await res.json();
  const a = it.address || {};
  const city =
    a.city || a.town || a.village || a.hamlet || a.municipality || a.suburb || a.locality;
  const region = a.state || a.county || a.region;
  const country = ((a.country_code || "")).toUpperCase();
  const label = city
    ? `${city}${region ? `, ${region}` : ""}${country ? `, ${country}` : ""}`
    : it.display_name;
  return { kind: "place", label, lat, lon, city, region, country, raw: it };
}

export default function LocationSelect({
  value,
  onChange,
  t = (s) => s, onBiasChange
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 400);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const ref = useRef(null);
  const [bias, setBias] = useState(() => {
    try { return JSON.parse(localStorage.getItem("locationBias")) || PL_CENTER; } catch { return PL_CENTER; }
  });

  useOutsideClick({ ref, handler: () => setOpen(false) });

  useEffect(() => {
    const q = (debounced || "").trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }

    const ac = new AbortController();
    setLoading(true);

    (async () => {
      const local = localCityHintsPL(q, 6);
      const remote = await searchPhotonCities(q, ac.signal, { bias, limit: 15 });
      setResults(mergeAndSort({ local, remote, q, bias }));
    })().catch(() => {}).finally(() => setLoading(false));

    return () => ac.abort();
  }, [debounced, bias]);

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    if (value.kind === "online") return t("location.online");
    if (value.kind === "anywhere") return t("location.anywhere");
    if (value.kind === "detect") return t("location.detect");
    return value.label ?? value;
  }, [value, t]);

  const applyBias = (b) => {
    setBias(b);
    localStorage.setItem("locationBias", JSON.stringify(b));
    onBiasChange && onBiasChange(b);
  };

  const pick = (opt) => {
    let val = opt?.city ?? opt?.kind;
    // ustaw bias wg wyboru
    if (opt?.kind === "anywhere") {
      applyBias(PL_CENTER);
      val = "";
    } else if (Number.isFinite(opt?.lat) && Number.isFinite(opt?.lon)) {
      applyBias({ lat: opt.lat, lon: opt.lon });
    }
    onChange && onChange(val);
    setOpen(false);
    setQuery("");
  };

  const detect = () => {
    if (!navigator.geolocation) return;
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const ac = new AbortController();
          const opt = await reverseGeocode(
            pos.coords.latitude,
            pos.coords.longitude,
            ac.signal
          );
          pick(opt);
        } finally {
          setLoading(false);
        }
      },
      () => setLoading(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };
  const showQuery = (open || (query?.length ?? 0) > 0);

  return (
    <Box position="relative" ref={ref} zIndex={1} alignSelf={{base: 'baseline', md: 'center'}}>
      <InputGroup flex="1" onClick={() => setOpen(true)}>
        <InputLeftElement>
          <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg" style={{"justify-self": "center"}}>
            <path d="M12.0819 12.0816L8.63892 15.5246C8.48835 15.6753 8.30955 15.7949 8.11275 15.8764C7.91594 15.958 7.70499 16 7.49195 16C7.2789 16 7.06795 15.958 6.87114 15.8764C6.67434 15.7949 6.49554 15.6753 6.34497 15.5246L2.90121 12.0816C1.99339 11.1738 1.37516 10.0171 1.12471 8.75785C0.874261 7.49862 1.00283 6.19341 1.49417 5.00726C1.98551 3.8211 2.81755 2.80728 3.88507 2.094C4.95259 1.38071 6.20765 1 7.49154 1C8.77543 1 10.0305 1.38071 11.098 2.094C12.1655 2.80728 12.9976 3.8211 13.4889 5.00726C13.9802 6.19341 14.1088 7.49862 13.8584 8.75785C13.6079 10.0171 12.9897 11.1738 12.0819 12.0816Z" stroke="#787878" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9.92584 7.4913C9.92584 8.13693 9.66937 8.75611 9.21285 9.21263C8.75632 9.66916 8.13714 9.92563 7.49152 9.92563C6.84589 9.92563 6.22671 9.66916 5.77019 9.21263C5.31366 8.75611 5.05719 8.13693 5.05719 7.4913C5.05719 6.84568 5.31366 6.2265 5.77019 5.76997C6.22671 5.31345 6.84589 5.05698 7.49152 5.05698C8.13714 5.05698 8.75632 5.31345 9.21285 5.76997C9.66937 6.2265 9.92584 6.84568 9.92584 7.4913Z" stroke="#787878" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </InputLeftElement>

        <Input
          border="0px"
          borderRadius="0"
          placeholder={t('location.search')}
          value={showQuery ? query : (selectedLabel || "")}
          fontFamily="'DM Sans', sans-serif"
          fontSize={{ base: '16px', md: '15px' }}
          fontWeight="400"
          lineHeight="normal"
          color="#787878"
          _placeholder={{ color: '#787878' }}
          sx={{ '--input-font-size': { base: '16px', md: '15px' } }}
          onFocus={() => {
            setOpen(true);
            if ((query ?? "") === "" && selectedLabel) {
              setQuery(selectedLabel);
            }
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            if(!(value?.kind || value?.label)) {
              onChange && onChange(e.target.value);
            }
            if (!open) setOpen(true);
          }}
          _focusVisible={{ shadow: "outline" }}
          _focus={{ shadow: "none" }}
          pl={10}
        />
      </InputGroup>

      {open && (
        <Box
          mt={2}
          position="absolute"
          left={0}
          right={0}
          bg="white"
          shadow="lg"
          rounded="md"
          zIndex={20}
          maxH="320px"
          overflowY="auto"
          p={2}
        >
          <Flex flexDirection="column" gap={1}>
            <OptionRow icon="🎯" label={t("location.detect")} onClick={detect} />
            <OptionRow icon="🌐" label={t("location.online")} onClick={() => pick({ kind: "online" })} />
            <OptionRow icon="🗺️" label={t("location.anywhere")} onClick={() => pick({ kind: "anywhere" })} />
            <Divider my={1} />

            {loading && (
              <Flex py={3} align="center" justify="center">
                <Spinner size="sm" mr={2} /> <Text fontSize="sm" color="#787878" fontFamily="body">{t("location.searching")}…</Text>
              </Flex>
            )}

            {!loading && results.map((r, i) => (
              <OptionRow
                key={`${r.kind}-${i}-${r.label}`}
                icon={<svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.0819 12.0816L8.63892 15.5246C8.48835 15.6753 8.30955 15.7949 8.11275 15.8764C7.91594 15.958 7.70499 16 7.49195 16C7.2789 16 7.06795 15.958 6.87114 15.8764C6.67434 15.7949 6.49554 15.6753 6.34497 15.5246L2.90121 12.0816C1.99339 11.1738 1.37516 10.0171 1.12471 8.75785C0.874261 7.49862 1.00283 6.19341 1.49417 5.00726C1.98551 3.8211 2.81755 2.80728 3.88507 2.094C4.95259 1.38071 6.20765 1 7.49154 1C8.77543 1 10.0305 1.38071 11.098 2.094C12.1655 2.80728 12.9976 3.8211 13.4889 5.00726C13.9802 6.19341 14.1088 7.49862 13.8584 8.75785C13.6079 10.0171 12.9897 11.1738 12.0819 12.0816Z" stroke="#787878" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.92584 7.4913C9.92584 8.13693 9.66937 8.75611 9.21285 9.21263C8.75632 9.66916 8.13714 9.92563 7.49152 9.92563C6.84589 9.92563 6.22671 9.66916 5.77019 9.21263C5.31366 8.75611 5.05719 8.13693 5.05719 7.4913C5.05719 6.84568 5.31366 6.2265 5.77019 5.76997C6.22671 5.31345 6.84589 5.05698 7.49152 5.05698C8.13714 5.05698 8.75632 5.31345 9.21285 5.76997C9.66937 6.2265 9.92584 6.84568 9.92584 7.4913Z" stroke="#787878" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>}
                label={r.label}
                onClick={() => pick(r)}
              />
            ))}

            {!loading && debounced && debounced.length >= 2 && results.length === 0 && (
              <Text px={3} py={2} color="gray.500" fontSize="sm" fontFamily="body">
                {t("No results")}
              </Text>
            )}
          </Flex>
        </Box>
      )}
    </Box>
  );
}

function OptionRow({ icon, label, onClick }) {
  return (
    <Flex
      as="button"
      w="100%"
      textAlign="left"
      align="center"
      gap={3}
      p={3}
      borderRadius="md"
      _hover={{ bg: 'gray.100', cursor: 'pointer' }}
      onClick={onClick}
    >
      <Box aria-hidden>{icon}</Box>
      <Text noOfLines={1} fontWeight="medium" color="#787878" fontFamily="body" fontSize="18px">{label}</Text>
    </Flex>
  );
}
